import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DocumentosPendientesChart from "./DocumentosPendientesChart.tsx";
import DocumentosFaltantesChart from "./DocumentosFaltantesChart.tsx";
import EstadoRevisionDocumentos from "./EstadoRevisionDocumentos.tsx";
import EstadoCredencialesChart from "./EstadoCredencialesChart.tsx";
import PersonalPorPuesto from "./PersonalPorPuesto.tsx";
import InformacionColaboradores from "./InformacionColaboradores.tsx";
import UltimasBajas from "./UltimasBajas.tsx";
import CambiosPuesto from "./CambiosPuesto.tsx";
import StatsCards from "./StatsCards.tsx";
import ContractsSummary from "./ContractsSummary.tsx";
import ImssContractsSummary from "./ImssContractsSummary.tsx";
import ContractsReportTable from "./ContractsReportTable.tsx";
import MovimientosPuestoDetalle from "./MovimientosPuestoDetalle.tsx";
import OrganigramaGraphs from "./OrganigramaGraphs.tsx";
import IncidentsStatsCard from "./IncidentsStatsCard.tsx";
import ChartSkeleton from "./ChartSkeleton.tsx";
import TransportStats from "./TransportStats.tsx";

interface RoutesGraphsProps {
  barChartData: any[];
  pieChartDocumentsData: any[];
  stackedBarData: any[];
  totalStats: {
    totalPartners: number;
    partnersWithCredentials: number;
    totalPendingDocuments: number;
  };
  data: any[];
  pendingReviewCount: number;
  reviewedToday: number;
  staffByPosition: any;
  unsubscribeStats: any;
  unsubscribeDays: number;
  setUnsubscribeDays: (days: number) => void;
  setUnsubscribeStats: (stats: any) => void;
  handleViewBranch: (branchId: number) => void;
  unsubscribeDateRange?: { startDate?: string; endDate?: string };
  onDateRangeChange?: (startDate?: string, endDate?: string) => void;
  styles: { [key: string]: React.CSSProperties };
  loading?: boolean;
}

const RoutesGraphs: React.FC<RoutesGraphsProps> = ({
  barChartData,
  pieChartDocumentsData,
  stackedBarData,
  totalStats,
  data,
  pendingReviewCount,
  reviewedToday,
  staffByPosition,
  unsubscribeStats,
  unsubscribeDays,
  setUnsubscribeDays,
  setUnsubscribeStats,
  handleViewBranch,
  unsubscribeDateRange,
  onDateRangeChange,
  styles,
  loading = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentHash = location.hash.replace("#", "") || "general";

  React.useEffect(() => {
    const styleId = "routes-graphs-skeleton-animation";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .skeleton-shimmer {
          background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  React.useEffect(() => {
    if (location.pathname === "/graficas" && !location.hash) {
      navigate("/graficas#general", { replace: true });
    }
  }, [location.pathname, location.hash, navigate]);

  if (currentHash === "general" || currentHash === "") {
    return (
      <>
        {loading ? (
          <div style={styles.statsGrid}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                style={{
                  ...styles.statCard,
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  className="skeleton-shimmer"
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    className="skeleton-shimmer"
                    style={{
                      height: "32px",
                      width: "60px",
                      borderRadius: "4px",
                      marginBottom: "8px",
                    }}
                  ></div>
                  <div
                    className="skeleton-shimmer"
                    style={{
                      height: "14px",
                      width: "120px",
                      borderRadius: "4px",
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <StatsCards
            data={data}
            totalStats={totalStats}
            pendingReviewCount={pendingReviewCount}
            styles={styles}
          />
        )}

        <div style={styles.twoColumnRow}>
          <div style={{ flex: "2 1 400px", display: "flex", minWidth: 0, maxWidth: "100%" }}>
            {loading ? (
              <ChartSkeleton
                height={400}
                headerTitle="Documentos Pendientes por Sucursal"
                headerIcon="fa-solid fa-chart-column"
              />
            ) : (
              <DocumentosPendientesChart
                barChartData={barChartData}
                handleViewBranch={handleViewBranch}
              />
            )}
          </div>
          <div style={{ flex: "1 1 320px", display: "flex", minWidth: 0, maxWidth: "100%" }}>
            {loading || !unsubscribeStats ? (
              <ChartSkeleton
                height={400}
                headerTitle="Información de colaboradores"
                headerIcon="fa-solid fa-user-minus"
              />
            ) : (
              <InformacionColaboradores
                unsubscribeStats={unsubscribeStats}
                unsubscribeDays={unsubscribeDays}
                setUnsubscribeDays={setUnsubscribeDays}
                setUnsubscribeStats={setUnsubscribeStats}
                onDateRangeChange={onDateRangeChange}
              />
            )}
          </div>
        </div>

        <div style={{ ...styles.twoColumnRow, marginTop: "20px" }}>
          <div
            style={{
              flex: "1 1 280px",
              display: "flex",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <div style={{ width: "100%", display: "flex", minWidth: 0 }}>
              {loading ? (
                <ChartSkeleton
                  height={260}
                  headerTitle="Últimas Bajas"
                  headerIcon="fa-solid fa-user-minus"
                />
              ) : (
                <UltimasBajas
                  startDate={unsubscribeDateRange?.startDate}
                  endDate={unsubscribeDateRange?.endDate}
                  onDateRangeChange={onDateRangeChange}
                />
              )}
            </div>
          </div>
          <div
            style={{
              flex: "1 1 280px",
              display: "flex",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <div style={{ width: "100%", display: "flex", minWidth: 0 }}>
              {loading ? (
                <ChartSkeleton
                  height={260}
                  headerTitle="Estado de Revisión"
                  headerIcon="fa-solid fa-clipboard-check"
                />
              ) : (
                <EstadoRevisionDocumentos
                  pendingReviewCount={pendingReviewCount}
                  reviewedToday={reviewedToday}
                />
              )}
            </div>
          </div>
          <div
            style={{
              flex: "1 1 280px",
              display: "flex",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <div style={{ width: "100%", display: "flex", minWidth: 0 }}>
              {loading ? (
                <ChartSkeleton
                  height={260}
                  headerTitle="Cambios de Puesto"
                  headerIcon="fa-solid fa-arrow-right-arrow-left"
                />
              ) : (
                <CambiosPuesto
                  startDate={unsubscribeDateRange?.startDate}
                  endDate={unsubscribeDateRange?.endDate}
                />
              )}
            </div>
          </div>
        </div>

        <div style={styles.twoColumnRow}>
          <div style={{ flex: "1 1 400px", minWidth: 0, maxWidth: "100%" }}>
            {loading || !staffByPosition ? (
              <ChartSkeleton
                height={350}
                headerTitle="Personal por Puesto"
                headerIcon="fa-solid fa-users-gear"
              />
            ) : (
              <PersonalPorPuesto staffByPosition={staffByPosition} />
            )}
          </div>
          <div style={{ flex: "1 1 400px", minWidth: 0, maxWidth: "100%" }}>
            {loading ? (
              <ChartSkeleton
                height={350}
                headerTitle="Documentos Más Faltantes"
                headerIcon="fa-solid fa-chart-pie"
              />
            ) : (
              <DocumentosFaltantesChart
                pieChartDocumentsData={pieChartDocumentsData}
              />
            )}
          </div>
        </div>

      </>
    );
  }

  if (currentHash === "documentos") {
    return (
      <>
        <StatsCards
          data={data}
          totalStats={totalStats}
          pendingReviewCount={pendingReviewCount}
          styles={styles}
        />

        <div style={styles.twoColumnRow}>
          <div style={{ flex: "2 1 400px", display: "flex", minWidth: 0, maxWidth: "100%" }}>
            <DocumentosPendientesChart
              barChartData={barChartData}
              handleViewBranch={handleViewBranch}
            />
          </div>
          <div style={{ flex: "1 1 320px", display: "flex", minWidth: 0, maxWidth: "100%" }}>
            <EstadoRevisionDocumentos
              pendingReviewCount={pendingReviewCount}
              reviewedToday={reviewedToday}
            />
          </div>
        </div>

        <div style={styles.twoColumnRow}>
          <DocumentosFaltantesChart
            pieChartDocumentsData={pieChartDocumentsData}
          />
          <EstadoCredencialesChart stackedBarData={stackedBarData} />
        </div>
      </>
    );
  }

  if (currentHash === "chilitos") {
    return (
      <>
        <div style={{ ...styles.twoColumnRow, alignItems: "stretch" }}>
          <PersonalPorPuesto staffByPosition={staffByPosition} />
          <InformacionColaboradores
            unsubscribeStats={unsubscribeStats}
            unsubscribeDays={unsubscribeDays}
            setUnsubscribeDays={setUnsubscribeDays}
            setUnsubscribeStats={setUnsubscribeStats} 
            onDateRangeChange={onDateRangeChange}
          />
        </div>

        <div style={{ ...styles.twoColumnRow, marginTop: "24px" }}>
          <div style={{ flex: 1, minWidth: "300px" }}>
          <UltimasBajas
            startDate={unsubscribeDateRange?.startDate}
            endDate={unsubscribeDateRange?.endDate}
            showChart
            onDateRangeChange={onDateRangeChange}
          />
          </div>
          <div style={{ flex: 1, minWidth: "300px" }}>
          <CambiosPuesto
            startDate={unsubscribeDateRange?.startDate}
            endDate={unsubscribeDateRange?.endDate}
            showChart
          />
          </div>
          <div style={{ flex: 1, minWidth: "300px" }}>
            <IncidentsStatsCard />
          </div>
        </div>

        <div style={{ marginTop: "16px" }}>
          <MovimientosPuestoDetalle />
        </div>

        {/* Reporte de Medios de Transporte - Al final */}
        <div style={{ marginTop: "24px" }}>
          <TransportStats />
        </div>
      </>
    );
  }

  if (currentHash === "contratos") {
    return (
      <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "16px" }}>
        <div style={{ display: "flex", gap: "16px" }}>
        <div style={{ flex: 1 }}>
          <ContractsSummary />
        </div>
        <div style={{ flex: 1 }}>
          <ImssContractsSummary />
          </div>
        </div>
        <div>
          <ContractsReportTable enabled={currentHash === "contratos"} />
        </div>
      </div>
    );
  }

  if (currentHash === "organigrama") {
    return (
      <div style={{ marginTop: "8px" }}>
        <OrganigramaGraphs />
      </div>
    );
  }

  return null;
};

export default RoutesGraphs;
